/**
 * Top shell for studio-style surfaces (batch tools today; same header can power
 * a unified home / tool hub later with different `title` / `subtitle` or brand
 * slot, e.g. Comic, Mascot, or Cartwheel + animated mark).
 */
import { Link } from 'react-router';

import type { NotificationSurface } from '@cartwhl/common/utils/notificationLocation';
import Text from '@cartwhl/ui/base/Text';
import HomeIcon from '@cartwhl/ui/icons/HomeIcon';
import Button from '@cartwhl/ui/input/Button';
import UserMenuWithCredits from '@cartwhl/ui/organisms/UserMenuWithCredits';

export const HEADER_ICON_BUTTON_CLASSNAME =
  'outline-hidden bg-primary border-gray-30 hover:bg-gray-15 data-[state=open]:bg-gray-15 dark:border-gray-75 hover:dark:bg-gray-75 data-[state=open]:dark:bg-gray-90 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-solid transition-colors';

export const SHELL_HEADER_TITLE_CLASSNAME =
  'text-primary h-5 shrink-0 truncate';

export const SHELL_HEADER_SUBTITLE_CLASSNAME =
  'text-tertiary ml-2 hidden h-full font-sans text-sm font-medium md:inline';

export type StudioShellHeaderProps = {
  /** Product or surface name, e.g. "Comic", "Motion Generation", "Mascot", "Cartwheel". */
  title: string;
  /** One-line gray descriptor next to the title; omit when empty. */
  subtitle?: string;
  /**
   * When set, home navigates via anchor (e.g. cross-app dashboard URL).
   * Otherwise uses in-app react-router `/`.
   */
  homeHref?: string;
  /** Accessible label for the home control. */
  homeAriaLabel?: string;
  /** Current route pathname for in-app notification pop-ups (from Router or navigation store). */
  currentPathname?: string;
  /** Cartwheel surface for notification pop-up matching. Defaults to `apps` for batch-tool shells. */
  currentSurface?: NotificationSurface;
};

export default function StudioShellHeader({
  title,
  subtitle,
  homeHref,
  homeAriaLabel = 'All applets',
  currentPathname,
  currentSurface = 'apps',
}: StudioShellHeaderProps) {
  /** Original shell was px-4 / sm:px-6; ×2 from that baseline. */
  const shellPadX = 'px-8 sm:px-12';

  const homeButton = (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      icon={<HomeIcon />}
      tabIndex={-1}
      aria-hidden
    />
  );

  return (
    <header
      className={`bg-primary border-gray-30 dark:border-gray-75 z-50 flex w-full shrink-0 items-center justify-between border-b py-3 ${shellPadX}`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-5">
        {homeHref ? (
          <a href={homeHref} aria-label={homeAriaLabel}>
            {homeButton}
          </a>
        ) : (
          <Link to="/" aria-label={homeAriaLabel}>
            {homeButton}
          </Link>
        )}
        <div className="flex min-w-0 items-center">
          <Text
            as="h1"
            variant="studio/title/lg"
            className={SHELL_HEADER_TITLE_CLASSNAME}
          >
            {title}
            {subtitle ? (
              <span className={SHELL_HEADER_SUBTITLE_CLASSNAME}>{subtitle}</span>
            ) : null}
          </Text>
        </div>
      </div>

      <UserMenuWithCredits
        currentPathname={currentPathname}
        currentSurface={currentSurface}
      />
    </header>
  );
}
